import { format, isBefore } from 'date-fns'
import get from 'lodash/get'
import * as Sentry from '@sentry/react'

import { getCountries } from '@fielded/fs-api/lib/service/tools/territory-lookup'
import { userIsAuthorised } from './auth'
import { TENANTS } from './tenants'
import { hasFeature } from './features'

export const roundTo2Decimals = (num) => {
  if (!num || isNaN(num)) return 0
  return Math.round(num * 100) / 100
}

export const byId = (items) => items
  .reduce((itemsByID, element) => {
    // use delve here to support grouping by nested props
    // ex element.location.zone
    itemsByID[get(element, 'id') || get(element, '_id')] = element
    return itemsByID
  }, {})

export const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

export const dateFormats = {
  long: 'd LLL yyyy',
  longWithTime: 'd LLL yyyy h:mmaaa',
  short: 'd LLL',
  shortWithTime: 'd LLL h:mma',
  shortWithTimeInverted: 'h:mma d LLL',
  shortWithFullMonth: 'd LLLL',
  shortWithDay: 'iii d LLL',
  expiry: 'LL/yy',
  quarter: 'qo',
  snapshotId: 'yyyy-LL-dd',
  printLong: 'iiii d LLLL yyyy',
  printShort: 'dd/LL/yyyy',
  printShortWithTime: 'dd/MM/yyyy, h:mm a',
  monthLong: 'LLLL',
  monthYearLong: 'LLLL yyyy',
  monthShort: 'LLL',
  dayOfWeek: 'iiii',
  weekOfYear: 'I',
  invoiceDateFormat: 'dd.LL.yy' // date format for wallet UI (transaction list and pinned payments)
}

export const mapMonthNamesToOptions = (monthNames) =>
  monthNames.map((monthName, index) => ({
    value: index + 1,
    label: `${(index + 1).toString().padStart(2, '0')} - ${monthName}`
  }))

export const monthOptions = () => mapMonthNamesToOptions(monthNames)

export const yearOptions = (start, addNYears) => {
  const startYear = start || new Date().getFullYear()
  const endYear = addNYears ? startYear + addNYears : startYear + 10
  const options = []

  for (let year = startYear; year <= endYear; year += 1) {
    options.push({
      value: year,
      label: year.toString()
    })
  }

  return options
}

export const formatDate = (date, dateFormat = 'short') => {
  // If a batch has no expiry date because it is untracked,
  // return an empty string so there are no obvious display errors
  if (!date) return ''
  return format(date, dateFormats[dateFormat])
}

export const isValidExpiry = (month, year) => {
  const currYear = new Date().getFullYear()
  return (month > 0 && month < 13 && year >= currYear && year < currYear + 40)
}

export const mapProgramsToOptions = (programs) => {
  return programs.map(program => ({
    value: program.id,
    label: program.name
  }))
}

export const mapCountriesToOptions = () => {
  return getCountries().map((val) => ({
    value: val.geoId,
    label: val.name
  }))
}

export const mapProductsToOptions = (products) => {
  return products.map(product => ({
    value: product._id,
    /**
      * We use fullName here to distingiush between e.g.
      * "fullName": "Long Lasting Insecticidal Net - Campaign",
      * "fullName": "Long Lasting Insecticidal Net - Routine",
      * which both have "name": "Long Lasting Insecticidal Nets (LLIN)"
      */
    label: product.fullName
  }))
}

export const getNameFromCollection = (collection, id) => {
  return collection[id] ? collection[id].name : id
}

/*
 * Pluralize a word.
 *
 * Given we currently only need to pluralize english regular nouns, we can use
 * a simple ternary operation.
 */
export const pluralize = (noun, count, plural) => {
  if (!noun) return
  const isPlural = count !== 1

  if (plural) {
    // if a custom plural was passed, for irregular nouns
    return isPlural ? plural : noun
  }

  if (noun.endsWith('y') && isPlural) {
    // if second last char is not a vowel, e.g. in day
    if (/[aeiou]/.test(noun.substring(0, noun.length - 2))) {
      return noun.replace(/.$/, 'ies')
    }
  }

  return !isPlural ? noun : `${noun}s`
}

/*
 * Get the ordinal of any number.
 * eg 6 => 6th and 23 => 23rd etc.
 */
export const getIndexOrdinals = (index) => {
  const ones = index % 10
  const tens = Math.floor(index / 10) % 10

  if (tens !== 1) {
    if (ones === 1) return `${index}st`
    if (ones === 2) return `${index}nd`
    if (ones === 3) return `${index}rd`
  }
  return `${index}th`
}

export const isTodayBeforeSetDate = (date) => {
  if (!date) {
    return false
  }

  const today = new Date()

  return isBefore(today, new Date(date))
}

export const getTenant = (config) => {
  return config.app
}

export const getUppercaseTenant = (config) => {
  return getTenant(config).toUpperCase()
}

/**
 * Tenant type and role checkers for multi-tenant apps like PSM and ShelfLife.
 *
 * 🔧 Naming conventions:
 * - Functions ending in `App` expect a `config` object.
 *   e.g., `isPsmCoreApp(config)` or `isShelflifeResellerApp(config)`
 * - Functions ending in `User` typically expect a `user` object.
 *   e.g., `isPsmWarehouseUser(user)`
 * - Some helpers combine both, accepting `config` and `user`.
 *   e.g., `isPsmResellerWarehouseUser(config, user)`
 * - At the usage site, we drop `App`/`User` suffixes for readability:
 *   e.g., `const isPsmCore = isPsmCoreApp(config)`
 *   e.g., `const isShelflifeCore = isShelflifeCoreApp(config)`
 *         `const isPsmWarehouse = isPsmWarehouseUser(user)`
 *         `const isPsmResellerWarehouse = isPsmResellerWarehouseUser(config, user)`
 *
 * 🧠 Key definitions:
 * - Core App: A tenant where `config.app === TENANTS.{X}` (e.g., PSM or Shelf Life itself)
 * - Based App: Any tenant where `config.appBase === TENANTS.{X}`.
 *   These apps run on the same backend/core platform but may have separate themes or branding.
 * - Reseller App: A based app that is *not* the core app — i.e., a sub-tenant powered by the core platform.
 *   For example: `EPN` is a PSM-based resellers;
 *
 * ⚠️ Special case:
 * - `EPN` is powered by PSM but is not a core app. So we use `isEpnApp(config)`, and usage should look like:
 *     `const isEpnUser = isEpnApp(config)`
 *   This avoids implying that EPN is equivalent to PSM in hierarchy.
 *
 * 📁 For full tenant hierarchy, see:
 * https://github.com/fielded/field-supply/blob/11da9bad320a0e3397ed153edf5d34e2c949f259/web/src/van-shared/utils/tenants.js#L3
 */

// ******
// SL
// ******

export const isShelflifeCoreApp = (config) => {
  return config && config.app === TENANTS.SHELFLIFE
}

// There can be apps (currently none, but we used to have some) that are not Shelf Life but use it underneath. We want them to work same, but have different theme.
export const isShelflifeBasedApp = (config) => {
  return config && config.appBase === TENANTS.SHELFLIFE
}

export const isShelflifeResellerApp = (config) => {
  return isShelflifeBasedApp(config) && !isShelflifeCoreApp(config)
}

// ******
// PSM
// ******

export const isPsmCoreApp = (config) => {
  return config && config.app === TENANTS.PSM
}

export const isPsmBasedApp = (config) => {
  return config && config.appBase === TENANTS.PSM
}

export const isPsmResellerApp = (config) => {
  return isPsmBasedApp(config) && !isPsmCoreApp(config)
}

export const isPsmNationalUser = (user) => {
  return userIsAuthorised(user, 'feature:userRole:psm-national')
}

export const isPsmStateUser = (user) => {
  return userIsAuthorised(user, 'feature:userRole:psm-state')
}

export const isPsmWarehouseUser = (user) => {
  return userIsAuthorised(user, 'feature:userRole:psm-warehouse')
}

export const isPsmFacilityUser = (user) => {
  return userIsAuthorised(user, 'feature:userRole:psm-facility')
}

export const isPsmDriverUser = (user) => {
  return userIsAuthorised(user, 'feature:userRole:psm-driver')
}

export const isPsmCarrierUser = (user) => {
  return userIsAuthorised(user, 'feature:userRole:psm-carrier')
}

export const isPsmResellerNationalUser = (config, user) => {
  return (
    isPsmResellerApp(config) && isPsmNationalUser(user)
  )
}

// Method used to check if a a national user can create new national users
// for PSM application resellers ie EPN users.
export const isPsmResellerWithAdminAccessUser = (config, user) => {
  return isPsmResellerApp(config) && userIsAuthorised(user, 'feature:settings')
}

export const isPsmResellerWarehouseUser = (config, user) => {
  return isPsmResellerApp(config) && isPsmWarehouseUser(user)
}

export const isPsmResellerFacilityUser = (config, user) => {
  return isPsmResellerApp(config) && isPsmFacilityUser(user)
}

// Since EPN is powered app (powered by PSM) it should not be named isEpnCoreApp
// It should be called isEpnApp and usage should be isEpnUser
// ie const isEpnUser = isEpnApp(config)
export const isEpnApp = (config) => {
  return config && config.app === TENANTS.EPN
}

export const isFacilityLevel = (location) => {
  return location && location.level === 'sdp'
}

export const checkIsSalesUser = (user) => {
  return userIsAuthorised(user, 'feature:userRole:sales')
}

export const userCanPackShipments = (user) => {
  return userIsAuthorised(user, 'feature:shipments:pack-shipments')
}

export const externalUser = (user) => {
  const externalUserRoles = [
    'userRole:psm-carrier',
    'userRole:psm-driver'
  ]
  if (!user || !user.roles) {
    return false
  }
  return user.roles.some(role => externalUserRoles.includes(role))
}

export const toTitleCase = (str) => {
  return str.split('')[0].toUpperCase() + str.slice(1)
}

export const isValidEmail = (email) => {
  if (!email) {
    return false
  }

  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,50}$/
  return emailRegex.test(email.trim())
}

export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return false
  }

  const phoneNumberRegex = /^[0-9+ ]+$/
  return phoneNumberRegex.test(phoneNumber.trim())
}

export const isValidCompletePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
    return false
  }

  const phoneWithoutCountryCode = phoneNumber.replace(/^\+\d{1,3}/, '').trim()
  return phoneWithoutCountryCode && phoneWithoutCountryCode.length > 0
}

// Basic password validation — only checks minimum length (8 chars).
// Does not enforce special characters, uppercase, or other complexity rules.
export const getPasswordValidationError = (password) => {
  const passwordMinLength = 8

  if (password.length < passwordMinLength) {
    return `Password must be at least ${passwordMinLength} characters long`
  }
  return null
}

// TODO: there will a follow-up PR to rename
// all `masterData` props in the shipments, reports, and analytics apps
// to masterDataShipments, masterDataReports and masterDataAnalytics respectively
export const updateCurrentMasterData = (currentState, masterDataToUpdate) => {
  const newState = { ...currentState }

  // this log it to debug a potential issue with user having both masterData and masterDataAnalytics props
  const shouldLogAnalyticsMasterDataShadowing = (
    masterDataToUpdate === 'masterDataAnalytics' &&
    newState.masterData &&
    newState.masterDataAnalytics &&
    newState.masterData !== newState.masterDataAnalytics
  )

  if (shouldLogAnalyticsMasterDataShadowing) {
    const masterDataProgramsType = typeof get(newState, 'masterData.programs')
    const analyticsProgramsType = typeof get(newState, 'masterDataAnalytics.programs')
    const looksLikeLegacyShadowing = (
      (masterDataProgramsType === 'undefined' || newState.masterData.programs == null) &&
      analyticsProgramsType === 'object'
    )

    if (looksLikeLegacyShadowing && !window.__analyticsMasterDataShadowingLogged) {
      // guard to run only once per page load
      window.__analyticsMasterDataShadowingLogged = true
      Sentry.captureMessage('analytics_master_data_shadowing_detected', {
        extra: {
          path: window.location && window.location.pathname,
          masterDataKeys: Object.keys(newState.masterData || {}),
          analyticsMasterDataKeys: Object.keys(newState.masterDataAnalytics || {}),
          masterDataProgramsType,
          analyticsProgramsType
        }
      })
    }
  }

  if (!newState.masterData) {
    newState.masterData = newState[masterDataToUpdate]
  }
  return newState
}

export const canViewBatchInfo = (config) => {
  return hasFeature(config, 'features.shipments.showBatchInfo')
}

export const showAnalyticsElements = (config) => {
  return get(config, 'features.analytics.showAnalyticsElements')
}

// Find out if analytics access is enabled for this user
export const hasAnalyticsAccess = (user, config) => {
  // Is it active for the tenant?
  const isFeatureEnabled = hasFeature(config.features, 'analytics')
  if (!isFeatureEnabled) return false

  // Get the list of roles excluded from analytics
  const nonAnalyticsRoles = get(config, 'features.analytics.nonAnalyticsRoles', [])

  // Check if the user has any role that is excluded
  const isExcluded = user.roles && user.roles.some((role) => (
    nonAnalyticsRoles.includes(role)
  ))

  // User has analytics access only if not excluded
  return !isExcluded
}

// Financial analytics is explicitly allowed via a dedicated role.
export const hasFinancialAnalyticsAccess = (user, config) => {
  const isFinancialAnalyticsEnabledForTheTenant = get(config, 'features.analytics.financialAnalytics', false)
  if (!isFinancialAnalyticsEnabledForTheTenant) return false

  return userIsAuthorised(user, 'feature:analytics:financial')
}

export const hasPGAnalyticsAccess = (config, user) => {
  return (
    hasFeature(config, 'features.usePGAnalytics') && userIsAuthorised(user, 'feature:analytics:operations-pg')
  )
}

export const hasPgComparisonIndicator = (config, indicator) => {
  const pgComparisonIndicators = get(config, 'features.analytics.pgComparisonIndicators', [])
  return pgComparisonIndicators.includes(indicator)
}
